import { useNavigate, useSearchParams } from 'react-router-dom'
import PropTypes from 'prop-types'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import useApi from './useApi'
import useFocus from './useFocus'

export const CurrentUserContext = createContext()

export function CurrentUserProvider({ children }) {
  const {
    currentUser,
    getBusiness,
    getUser,
    isSessionValid,
    setCurrentUser,
    signInWithEmail,
    signOut,
  } = useApi()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const isFocused = useFocus()
  const mountTimeRef = useRef(Date.now())

  const [business, setBusiness] = useState(null)

  const fetchUser = useCallback(async () => {
    if (!currentUser?.id) return
    try {
      const { data } = await getUser({ userId: currentUser.id })
      setCurrentUser(data)
    } catch (_err) {
      // 401 or other error: don't throw (avoids error overlay on reopen)
    }
  }, [currentUser?.id, getUser, setCurrentUser])

  const fetchBusiness = useCallback(async () => {
    if (!currentUser?.business_id) return
    try {
      const { data } = await getBusiness(currentUser.business_id)
      setBusiness(data)
    } catch (_err) {
      // 401 or other error: don't throw (avoids error overlay on reopen)
      setBusiness(null)
    }
  }, [currentUser?.business_id, getBusiness])

  const locale = useMemo(
    () =>
      (
        searchParams.get('locale') ||
        currentUser?.language ||
        window.navigator?.language ||
        window.navigator?.userLanguage
      )?.split('-')?.[0] || 'en',
    [currentUser?.language, searchParams],
  )

  const betaFeatures = useMemo(
    () =>
      currentUser?.beta_features?.map(betaFeature => betaFeature.name) || [],
    [currentUser?.beta_features],
  )

  const hasBetaFeature = useCallback(
    feature => betaFeatures.includes(feature),
    [betaFeatures],
  )

  const context = useMemo(
    () => ({
      betaFeatures,
      business,
      currentUser,
      fetchBusiness,
      fetchUser,
      hasBetaFeature,
      locale,
      setCurrentUser,
      signInWithEmail,
      signOut,
    }),
    [
      betaFeatures,
      business,
      currentUser,
      fetchBusiness,
      fetchUser,
      hasBetaFeature,
      locale,
      setCurrentUser,
      signInWithEmail,
      signOut,
    ],
  )

  const checkSessionValid = useCallback(async () => {
    try {
      if (!!currentUser?.id && !(await isSessionValid())) {
        signOut()
        navigate('/login')
      }
    } catch (_err) {
      // Don't sign out on network/other errors; avoid unhandled rejection
    }
  }, [currentUser?.id, isSessionValid, signOut, navigate])

  // Session check only on window focus, and only after we've been mounted for a bit.
  // This prevents "open link in new tab" from immediately clearing auth (shared localStorage)
  // when the new tab gets focus and fires a 401 before the app is ready.
  const FOCUS_CHECK_DELAY_MS = 2500
  useEffect(() => {
    if (!isFocused) return
    const elapsed = Date.now() - mountTimeRef.current
    if (elapsed < FOCUS_CHECK_DELAY_MS) return
    checkSessionValid().catch(err => {
      console.error('CurrentUserProvider: session check failed', err)
    })
  }, [isFocused, checkSessionValid])

  useEffect(() => {
    fetchBusiness().catch(err => {
      console.error('CurrentUserProvider: fetchBusiness failed', err)
    })
  }, [fetchBusiness])

  return (
    <CurrentUserContext.Provider value={context}>
      {children}
    </CurrentUserContext.Provider>
  )
}

CurrentUserProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export const useCurrentUser = () => useContext(CurrentUserContext)
